import { current } from '../helpers'
import { bulkTodoTodosetsPath, bulkTodoTodosetTodolistsPath } from '../routes'
import SelectAllController from './select_all_controller'

const TODOSETS_FRAME_ID = 'bulk_todos_todosets'
const TODOLISTS_FRAME_ID = 'bulk_todos_todolists'
const TODOLIST_INPUT_SELECTOR = 'bulk_todos[todolist_id]'

export default class extends SelectAllController {
  static targets = [
    'todosContent', 'tableContainer', 'tableBody', 'rowCount', 'formatBadge',
    'destinationSection', 'projectSelect', 'exampleDialog', 'scrollHint',
    'submitButton', 'buttonCount',
    'projectStep', 'todosetStep', 'todolistStep'
  ]

  connect () {
    const tsFrame = document.getElementById(TODOSETS_FRAME_ID)
    if (tsFrame) this.initialTodosetsHTML = tsFrame.innerHTML
    const tlFrame = document.getElementById(TODOLISTS_FRAME_ID)
    if (tlFrame) this.initialTodolistsHTML = tlFrame.innerHTML

    this.onScroll = this.onScroll.bind(this)
    window.addEventListener('scroll', this.onScroll, { passive: true })
    window.addEventListener('resize', this.onScroll, { passive: true })
    document.addEventListener('turbo:frame-load', this.onScroll)
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(this.onScroll)
      this.resizeObserver.observe(document.body)
    }
    this.onScroll()
  }

  disconnect () {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onScroll)
    document.removeEventListener('turbo:frame-load', this.onScroll)
    this.resizeObserver?.disconnect()
  }

  onScroll () {
    if (!this.hasScrollHintTarget) return
    const docHeight = document.documentElement.scrollHeight
    const viewport = window.innerHeight
    const scrolled = window.scrollY
    const distanceFromBottom = docHeight - (scrolled + viewport)
    const isScrollable = docHeight > viewport + 100
    const shouldShow = isScrollable && distanceFromBottom > 200
    this.scrollHintTarget.style.opacity = shouldShow ? '1' : '0'
  }

  boxTitles () {
    return this.todosContentTarget.value.split('\n').map((l) => l.trim()).filter(Boolean)
  }

  syncFromBox () {
    const titles = this.boxTitles()
    const cards = Array.from(this.tableBodyTarget.querySelectorAll('[data-todo-index]'))

    titles.forEach((title, i) => {
      const card = cards[i]
      if (card) {
        this.setCardTitle(card, title)
      } else {
        this.appendTodo({ content: title, description: '', due_on: '' })
      }
    })
    cards.slice(titles.length).forEach((card) => card.remove())

    this.reindexRows()
    this.afterRowsChanged()
  }

  pasteContent (event) {
    const text = event.clipboardData?.getData('text/plain') ?? ''
    if (!text.includes('\t')) return

    event.preventDefault()
    const rows = text.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
      const [content, description, dueOn] = line.split('\t')
      return {
        content: (content || '').trim(),
        description: (description || '').trim(),
        due_on: (dueOn || '').trim()
      }
    })
    if (rows.length === 0) return

    const titles = this.boxTitles().concat(rows.map((r) => r.content))
    this.todosContentTarget.value = titles.join('\n')
    rows.forEach((row) => this.appendTodo(row))
    this.reindexRows()
    this.afterRowsChanged()
  }

  clearContent () {
    this.todosContentTarget.value = ''
    this.syncFromBox()
    this.todosContentTarget.focus()
  }

  openExample () {
    this.exampleDialogTarget.showModal()
  }

  closeExample () {
    this.exampleDialogTarget.close()
  }

  dialogBackdropClick (event) {
    if (event.target === this.exampleDialogTarget) {
      this.exampleDialogTarget.close()
    }
  }

  selectProject () {
    this.resetTodolists()
    this.setStepState(this.todolistStepTarget, 'disabled')
    if (this.projectId) {
      this.loadTodosets()
      this.setStepState(this.projectStepTarget, 'done')
      this.setStepState(this.todosetStepTarget, 'active')
    } else {
      this.resetTodosets()
      this.setStepState(this.projectStepTarget, 'active')
      this.setStepState(this.todosetStepTarget, 'disabled')
    }
    this.updateSubmitButtonState()
  }

  selectTodoset ({ target }) {
    this.loadTodolists(target.value)
    this.setStepState(this.todosetStepTarget, 'done')
    this.setStepState(this.todolistStepTarget, 'active')
    this.updateSubmitButtonState()
  }

  selectTodolist () {
    this.setStepState(this.todolistStepTarget, 'done')
    this.updateSubmitButtonState()
  }

  toggleDestinationSection () {
    if (this.hasTodos) {
      this.submitButtonVisible = true
      if (!this.projectId) this.setStepState(this.projectStepTarget, 'active')
    } else {
      this.submitButtonVisible = false
      this.setStepState(this.projectStepTarget, 'disabled')
      this.setStepState(this.todosetStepTarget, 'disabled')
      this.setStepState(this.todolistStepTarget, 'disabled')
      this.resetTodosets()
      this.resetTodolists()
    }
  }

  loadTodosets () {
    const frame = document.getElementById(TODOSETS_FRAME_ID)
    if (!frame) return
    this.showSkeleton(frame, this.cardsGridSkeleton())
    frame.src = bulkTodoTodosetsPath(current.account.id, this.projectId)
  }

  loadTodolists (todosetId) {
    const frame = document.getElementById(TODOLISTS_FRAME_ID)
    if (!frame) return
    this.showSkeleton(frame, this.listSkeleton())
    frame.src = bulkTodoTodosetTodolistsPath(current.account.id, this.projectId, todosetId)
  }

  resetTodosets () {
    const frame = document.getElementById(TODOSETS_FRAME_ID)
    if (!frame || !this.initialTodosetsHTML) return
    frame.removeAttribute('src')
    frame.innerHTML = this.initialTodosetsHTML
  }

  resetTodolists () {
    const frame = document.getElementById(TODOLISTS_FRAME_ID)
    if (!frame || !this.initialTodolistsHTML) return
    frame.removeAttribute('src')
    frame.innerHTML = this.initialTodolistsHTML
  }

  updateSubmitButtonState () {
    this.submitButtonDisabled = !(this.hasTodos && this.todolistSelected)
    this.buttonCountContent = this.todoCount
  }

  // eslint-disable-next-line accessor-pairs
  set buttonCountContent (count) {
    if (!this.hasButtonCountTarget) return
    this.buttonCountTarget.textContent = count === 1
      ? '1 to-do'
      : `${count} to-dos`
  }

  get todolistSelected () {
    return !!this.element.querySelector(`input[name="${TODOLIST_INPUT_SELECTOR}"]:checked`)
  }

  appendTodo (todo) {
    const index = this.todoCount
    const el = this.createTodoElement(todo, index)
    this.tableBodyTarget.appendChild(el)
    this.autosizeTodo(el)
    return el
  }

  afterRowsChanged () {
    const count = this.todoCount
    this.tableContainerTarget.classList.toggle('hidden', count === 0)
    if (this.hasRowCountTarget) this.rowCountTarget.textContent = String(count)
    this.updateFormatBadge()
    this.toggleDestinationSection()
    this.updateSubmitButtonState()
    requestAnimationFrame(() => this.onScroll())
  }

  refreshAfterEdit () {
    this.updateFormatBadge()
    this.updateSubmitButtonState()
  }

  setCardTitle (card, title) {
    const display = card.querySelector('[data-title-display]')
    if (display) {
      display.textContent = title || 'Untitled'
      display.classList.toggle('text-gray-300', !title)
      display.classList.toggle('text-gray-900', !!title)
    }
    const hidden = card.querySelector('input[name*="[content]"]')
    if (hidden) hidden.value = title
  }

  updateFormatBadge () {
    if (!this.hasFormatBadgeTarget) return
    if (this.todoCount === 0) {
      this.formatBadgeTarget.textContent = ''
      return
    }
    const rows = this.tableBodyTarget.querySelectorAll('[data-todo-index]')
    const hasExtras = Array.from(rows).some((el) => {
      const desc = el.querySelector('textarea[name*="[description]"]')?.value.trim()
      const date = el.querySelector('input[name*="[due_on]"]')?.value
      return desc || date
    })
    this.formatBadgeTarget.textContent = hasExtras ? ' · with details' : ' · simple list'
  }

  autosize (event) {
    const el = event.target
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }

  autosizeTodo (todo) {
    requestAnimationFrame(() => {
      todo.querySelectorAll('textarea').forEach((el) => {
        el.style.height = 'auto'
        el.style.height = `${el.scrollHeight}px`
      })
    })
  }

  createTodoElement (todo, index) {
    const el = document.createElement('div')
    el.className = 'group rounded-2xl border border-gray-100 bg-white p-4 hover:shadow-100 transition'
    el.dataset.todoIndex = index

    const parsedDate = this.parseDate(todo.due_on)
    const dateLabel = parsedDate ? this.formatDateLabel(parsedDate) : 'Add date'

    el.innerHTML = `
      <div class="flex items-start gap-3">
        <div class="flex-1 min-w-0 space-y-0.5">
          <input type="hidden" name="bulk_todos[todos][${index}][content]">
          <p data-title-display class="text-base font-medium text-gray-900 truncate"></p>
          <textarea name="bulk_todos[todos][${index}][description]"
                    rows="1"
                    placeholder="Add notes…"
                    class="block w-full bg-transparent border-0 p-0 text-sm text-gray-700 focus:ring-0 focus:outline-none placeholder:text-gray-300 resize-none min-h-[1.25rem]"
                    data-action="input->bulk-todos#autosize input->bulk-todos#refreshAfterEdit"></textarea>
        </div>

        <div class="flex items-center gap-1 shrink-0">
          <button type="button"
                  class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-gray-700 text-xs font-medium hover:bg-gray-50 hover:border-gray-400 transition shrink-0"
                  data-action="click->bulk-todos#openDatePicker">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M16 2c.183 0 .355 .05 .502 .135l.033 .02c.28 .177 .465 .49 .465 .845v1h1a3 3 0 0 1 2.995 2.824l.005 .176v12a3 3 0 0 1 -2.824 2.995l-.176 .005h-12a3 3 0 0 1 -2.995 -2.824l-.005 -.176v-12a3 3 0 0 1 2.824 -2.995l.176 -.005h1v-1a1 1 0 0 1 .514 -.874l.093 -.046l.066 -.025l.1 -.029l.107 -.019l.12 -.007q .083 0 .161 .013l.122 .029l.04 .012l.06 .023c.328 .135 .568 .44 .61 .806l.007 .117v1h6v-1a1 1 0 0 1 1 -1m3 7h-14v9.625c0 .705 .386 1.286 .883 1.366l.117 .009h12c.513 0 .936 -.53 .993 -1.215l.007 -.16z"/>
              <path d="M9.015 13a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
              <path d="M13.015 13a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
              <path d="M17.02 13a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
              <path d="M12.02 15a1 1 0 0 1 0 2a1.001 1.001 0 1 1 -.005 -2z"/>
              <path d="M9.015 16a1 1 0 0 1 -1 1a1.001 1.001 0 1 1 -.005 -2c.557 0 1.005 .448 1.005 1"/>
            </svg>
            <span data-date-label>${dateLabel}</span>
          </button>
          <input type="date"
                 name="bulk_todos[todos][${index}][due_on]"
                 value="${parsedDate}"
                 class="sr-only"
                 data-bulk-todos-date-input
                 data-action="change->bulk-todos#updateDateLabel">

          <button type="button"
                  class="w-8 h-8 rounded-full flex items-center justify-center text-gray-300 hover:bg-red-50 hover:text-red-500 transition"
                  data-action="click->bulk-todos#removeRow"
                  title="Remove">
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
              <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
              <path d="M20 6a1 1 0 0 1 .117 1.993l-.117 .007h-.081l-.919 11a3 3 0 0 1 -2.824 2.995l-.176 .005h-8c-1.598 0 -2.904 -1.249 -2.992 -2.75l-.005 -.167l-.923 -11.083h-.08a1 1 0 0 1 -.117 -1.993l.117 -.007zm-10 4a1 1 0 0 0 -1 1v6a1 1 0 0 0 2 0v-6a1 1 0 0 0 -1 -1m4 0a1 1 0 0 0 -1 1v6a1 1 0 0 0 2 0v-6a1 1 0 0 0 -1 -1"/>
              <path d="M14 2a2 2 0 0 1 2 2a1 1 0 0 1 -1.993 .117l-.007 -.117h-4l-.007 .117a1 1 0 0 1 -1.993 -.117a2 2 0 0 1 1.85 -1.995l.15 -.005z"/>
            </svg>
          </button>
        </div>
      </div>
    `

    this.setCardTitle(el, todo.content)
    const descTa = el.querySelector('textarea[name*="[description]"]')
    if (descTa) descTa.value = todo.description

    return el
  }

  openDatePicker (event) {
    const todo = event.currentTarget.closest('[data-todo-index]')
    if (!todo) return
    const input = todo.querySelector('[data-bulk-todos-date-input]')
    if (!input) return
    if (typeof input.showPicker === 'function') {
      input.showPicker()
    } else {
      input.focus()
      input.click()
    }
  }

  updateDateLabel (event) {
    const input = event.target
    const todo = input.closest('[data-todo-index]')
    const label = todo?.querySelector('[data-date-label]')
    if (label) {
      label.textContent = input.value ? this.formatDateLabel(input.value) : 'Add date'
    }
    this.refreshAfterEdit()
  }

  formatDateLabel (isoDate) {
    if (!isoDate) return 'Add date'
    const [y, m, d] = isoDate.split('-').map(Number)
    const date = new Date(y, m - 1, d)
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  removeRow (event) {
    const todo = event.target.closest('[data-todo-index]')
    if (!todo) return
    const cards = Array.from(this.tableBodyTarget.querySelectorAll('[data-todo-index]'))
    const position = cards.indexOf(todo)
    const titles = this.boxTitles()
    if (position > -1 && position < titles.length) {
      titles.splice(position, 1)
      this.todosContentTarget.value = titles.join('\n')
    }
    todo.remove()
    this.reindexRows()
    this.afterRowsChanged()
  }

  reindexRows () {
    const todos = this.tableBodyTarget.querySelectorAll('[data-todo-index]')
    todos.forEach((todo, index) => {
      todo.dataset.todoIndex = index
      todo.querySelectorAll('input, textarea').forEach((field) => {
        field.name = field.name.replace(/\[todos\]\[\d+\]/, `[todos][${index}]`)
      })
    })
  }

  parseDate (dateString) {
    if (!dateString) return ''
    const formats = [
      /^(\d{4})-(\d{1,2})-(\d{1,2})$/,
      /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/,
      /^([A-Za-z]+)\s+(\d{1,2})(?:,?\s+(\d{2,4}))?$/
    ]

    let match = dateString.match(formats[0])
    if (match) {
      return `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`
    }

    match = dateString.match(formats[1])
    if (match) {
      let year = match[3]
      if (year.length === 2) year = parseInt(year) > 50 ? `19${year}` : `20${year}`
      return `${year}-${match[1].padStart(2, '0')}-${match[2].padStart(2, '0')}`
    }

    match = dateString.match(formats[2])
    if (match) {
      const months = {
        jan: '01',
        january: '01',
        feb: '02',
        february: '02',
        mar: '03',
        march: '03',
        apr: '04',
        april: '04',
        may: '05',
        jun: '06',
        june: '06',
        jul: '07',
        july: '07',
        aug: '08',
        august: '08',
        sep: '09',
        september: '09',
        oct: '10',
        october: '10',
        nov: '11',
        november: '11',
        dec: '12',
        december: '12'
      }
      const month = months[match[1].toLowerCase()]
      if (month) {
        let year = match[3] || new Date().getFullYear().toString()
        if (year.length === 2) year = parseInt(year) > 50 ? `19${year}` : `20${year}`
        return `${year}-${month}-${match[2].padStart(2, '0')}`
      }
    }
    return ''
  }

  get todoCount () {
    return this.tableBodyTarget.querySelectorAll('[data-todo-index]').length
  }

  get hasTodos () {
    const contents = this.tableBodyTarget.querySelectorAll('input[name*="[content]"]')
    return Array.from(contents).some((field) => field.value.trim())
  }
}
